import React, { FC, memo, useCallback, useMemo } from 'react';

import { Pressable } from 'react-native';

import { useTranslation } from 'react-i18next';
import { StyleProp } from 'react-native/Libraries/StyleSheet/StyleSheet';
import { ViewStyle } from 'react-native/Libraries/StyleSheet/StyleSheetTypes';

import { EPGChannel } from 'custom_modules/react-native-epg';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { RBChannel } from 'store/railsBuilder/reducer.types';

import isFunction from 'utils/function';
import { isWeb } from 'utils/responsiveness';

import { EpgChannelExtra } from 'screens/Epg/types.ts';

import ChannelCard from 'components/Ribbon/components/ChannelCard/ChannelCard';

import styles from './styles';

type PropTypes = {
    channel: EPGChannel<EpgChannelExtra>;
    index: number;
    totalNumberOfItems: number;
    selected: boolean;
    onPress: (channel: EPGChannel<EpgChannelExtra>, index: number) => void;
    style?: StyleProp<ViewStyle>;
    styleSelected?: StyleProp<ViewStyle>;
};

const {
    EPG: { ACCESSIBILITY_EPG_CHANNEL_CARD },
} = LANGUAGE_KEYS;

const ChannelItem: FC<PropTypes> = ({
    channel,
    index,
    totalNumberOfItems,
    selected,
    onPress,
    style,
    styleSelected,
}) => {
    const { t } = useTranslation();

    const rbChannel = useMemo<Partial<RBChannel>>(
        () => ({
            // @ts-ignore
            editorial: {
                id: channel.id,
                longName: channel.name,
            },
        }),
        [channel],
    );

    const handleChannelPress = useCallback(() => {
        if (isFunction(onPress)) {
            onPress(channel, index);
        }
    }, [channel, index, onPress]);

    return (
        <Pressable
            style={[
                styles.root,
                selected && styles.selected,
                isWeb && {
                    transform: [{ translateY: selected ? 0 : 15 }],
                },
                style,
                selected && styleSelected,
            ]}
            onPress={handleChannelPress}
            accessibilityLabel={t(ACCESSIBILITY_EPG_CHANNEL_CARD, {
                'Channel name': channel.name || '',
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            })}
            role="button"
        >
            <ChannelCard
                item={rbChannel}
                index={index}
                totalNumberOfItems={totalNumberOfItems}
                style={styles.channelCard}
                accessible={false}
            />
        </Pressable>
    );
};

export default memo(ChannelItem);
